import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Modal } from '@spirit-testing/component-library';
import { FIELD_LENGTHS } from '../../shared/field-lengths';
import { PaketBelegung, PaketDialogErgebnis, PaketTestfall } from './paket-testfall-dialog.types';

/**
 * Dialog „Neues Testpaket“ bzw. „Testfälle für „…““ (NEX-2291, `PaketTestfallDialog` in
 * page-release-planung.jsx:269-322). `paketKey = null` legt ein Paket an. Testfälle anderer Pakete
 * sind gesperrt, damit jeder Testfall in höchstens einem Paket liegt.
 *
 * Der Dialog liegt im `<form>` des Testplan-Formulars: Alle Knöpfe sind `type="button"`, und die
 * Eingabetaste in einem Feld sendet nichts ab.
 */
@Component({
  selector: 'app-paket-testfall-dialog',
  imports: [Modal, TranslatePipe, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule],
  templateUrl: './paket-testfall-dialog.component.html',
  styleUrl: './paket-testfall-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaketTestfallDialogComponent {
  private readonly translate = inject(TranslateService);

  readonly testfaelle = input.required<readonly PaketTestfall[]>();
  readonly pakete = input<readonly PaketBelegung[]>([]);
  readonly paketKey = input<string | null>(null);
  readonly uebernehmen = output<PaketDialogErgebnis>();
  readonly abgebrochen = output<void>();

  readonly nameMax = FIELD_LENGTHS.TEST_PACKAGE_NAME_MAX;

  readonly paket = computed(() => {
    const key = this.paketKey();
    return key === null ? null : (this.pakete().find((p) => p.key === key) ?? null);
  });

  readonly name = linkedSignal<string>(
    () =>
      this.paket()?.name ??
      this.translate.instant('testPlans.packageDialog.defaultName', { n: this.pakete().length + 1 }),
  );
  readonly suche = signal('');

  /** Nur Testfälle des Plans: Im Paket können noch abgewählte Testfälle stehen. */
  private readonly ausgangswahl = computed<ReadonlySet<string>>(() => {
    const imPaket = new Set(this.paket()?.testCaseIds ?? []);
    return new Set(
      this.testfaelle()
        .filter((t) => imPaket.has(t.id))
        .map((t) => t.id),
    );
  });
  readonly wahl = linkedSignal<ReadonlySet<string>>(() => new Set(this.ausgangswahl()));

  private readonly nameFeld = viewChild<ElementRef<HTMLInputElement>>('nameFeld');

  /** Testfall-ID → Name des anderen Pakets, das ihn schon enthält. */
  private readonly belegt = computed(() => {
    const eigenes = this.paketKey();
    const belegt = new Map<string, string>();
    for (const p of this.pakete()) {
      if (p.key === eigenes) continue;
      for (const id of p.testCaseIds) belegt.set(id, p.name);
    }
    return belegt;
  });

  readonly zeilen = computed(() => {
    const q = this.suche().trim().toLowerCase();
    const belegt = this.belegt();
    const wahl = this.wahl();
    return this.testfaelle()
      .filter((t) => !q || `${t.key} ${t.title} ${t.tags.join(' ')}`.toLowerCase().includes(q))
      .map((t) => ({ testfall: t, belegtIn: belegt.get(t.id) ?? null, gewaehlt: wahl.has(t.id) }));
  });

  private readonly freie = computed(() => this.zeilen().filter((z) => z.belegtIn === null));
  readonly keineFreien = computed(() => this.freie().length === 0);
  readonly alleFreienGewaehlt = computed(() => !this.keineFreien() && this.freie().every((z) => z.gewaehlt));

  /** Beim bestehenden Paket erst nach einer Änderung der Auswahl (CLAUDE-FE.md, Edit-Modals). */
  private readonly unveraendert = computed(() => {
    const ausgang = this.ausgangswahl();
    const wahl = this.wahl();
    return !!this.paket() && ausgang.size === wahl.size && [...ausgang].every((id) => wahl.has(id));
  });

  constructor() {
    afterNextRender(() => this.nameFeld()?.nativeElement.focus());
  }

  readonly uebernehmenGesperrt = computed(() => this.wahl().size === 0 || !this.name().trim() || this.unveraendert());

  umschalten(id: string): void {
    this.wahl.update((wahl) => {
      const neu = new Set(wahl);
      if (!neu.delete(id)) neu.add(id);
      return neu;
    });
  }

  alleFreienUmschalten(): void {
    const freie = this.freie().map((z) => z.testfall.id);
    const abwaehlen = this.alleFreienGewaehlt();
    this.wahl.update((wahl) => {
      const neu = new Set(wahl);
      for (const id of freie) {
        if (abwaehlen) neu.delete(id);
        else neu.add(id);
      }
      return neu;
    });
  }

  nichtAbsenden(event: Event): void {
    event.preventDefault();
  }

  bestaetigen(): void {
    if (this.uebernehmenGesperrt()) return;
    const wahl = this.wahl();
    this.uebernehmen.emit({
      name: this.name().trim(),
      testCaseIds: this.testfaelle()
        .filter((t) => wahl.has(t.id))
        .map((t) => t.id),
    });
  }

  /** `lib-modal` meldet den Klick auf den Hintergrund (und ab 2.0 Esc) als `visibleChange(false)`. */
  beimSchliessenDurchModal(sichtbar: boolean): void {
    if (!sichtbar) this.abgebrochen.emit();
  }
}
